// hooks/useKycHook.js
import { useState, useCallback, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import {
  verifyNIN, verifyDriverLicense,
  verifySelfie, getVerificationStatus, verifyBikerLicense
} from '../Redux/kycSlice';
import { updateRunner } from '../Redux/authSlice';

const SINGLE_DOC_FLEETS = ['pedestrian', 'cycling'];
const getSecondDocType = (fleetType) => {
  if (SINGLE_DOC_FLEETS.includes(fleetType)) return null;
  return fleetType === 'bike' ? 'bikerLicense' : 'driverLicense';
};
const secondDocLabel = (docType) => docType === 'bikerLicense' ? "Biker's License" : "Driver's License";

// ─── Maps server kycStatus → the correct step to resume from ─────────────────
// Called once after a returning user completes OTP, before startKycFlow runs.
const resolveResumeStep = (kycStatus = {}, fleetType) => {
  const { ninStatus, driverLicenseStatus, bikerLicenseStatus, selfieVerified } = kycStatus;

  const secondDocType = getSecondDocType(fleetType);
  const ninDone = ['verified', 'pending', 'pending_review'].includes(ninStatus);
  const secondDocStatus = secondDocType === 'bikerLicense' ? bikerLicenseStatus : driverLicenseStatus;
  const secondDocDone = !secondDocType || ['verified', 'pending', 'pending_review'].includes(secondDocStatus);
  const needsSecondDoc = secondDocType && !secondDocDone;

  if (selfieVerified || kycStatus.selfieStatus === 'pending_review') return 6;
  if (ninDone && secondDocDone) return 3;
  if (ninDone && needsSecondDoc) return { step: 2, nextDoc: secondDocType };
  return null;
};

export const useKycHook = (runnerId, fleetType,) => {
  const dispatch = useDispatch();
  const [kycStep, setKycStep] = useState(null);

  const [kycStatus, setKycStatus] = useState({
    documentVerified: false,
    selfieVerified: false,
    overallVerified: false
  });
  const lastCheckedStatusRef = useRef(null);
  const [showConnectButton, setShowConnectButton] = useState(false);

  // bump this string any time kyc_* localStorage shape/logic changes again
  const KYC_STORAGE_VERSION = '4';
  const isReturningUserRef = useRef(false);

  const isAlreadyVerifiedRef = useRef(false);
  const capturedIdPhotoRef = useRef(null);
  const capturedSelfiePhotoRef = useRef(null);
  const kycInitiated = useRef(false);
  const verifyInProgress = useRef(false);
  // Track which doc is currently being collected: 'nin' | 'driverLicense'
  const [currentDocType, setCurrentDocTypeState] = useState('nin');
  const currentDocTypeRef = useRef('nin');
  const kycServerStatusRef = useRef({ nin: 'not_submitted', driverLicense: 'not_submitted', bikerLicense: 'not_submitted', selfie: 'not_submitted' });


  useEffect(() => {
    const currentVersion = localStorage.getItem('kyc_storage_version');
    if (currentVersion !== KYC_STORAGE_VERSION) {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith('kyc_')) {
          localStorage.removeItem(key);
        }
      });
      localStorage.setItem('kyc_storage_version', KYC_STORAGE_VERSION);
    }
  }, []);

  useEffect(() => {
    kycInitiated.current = false;
    verifyInProgress.current = false;
    capturedIdPhotoRef.current = null;
    capturedSelfiePhotoRef.current = null;
    currentDocTypeRef.current = 'nin';
    setCurrentDocTypeState('nin');
    isReturningUserRef.current = false;
    setKycStep(null);
    setKycStatus({ documentVerified: false, selfieVerified: false, overallVerified: false });

    if (runnerId) localStorage.removeItem(`kyc_status_${runnerId}`);
  }, [runnerId]);

  useEffect(() => {
    if (!runnerId || kycStep !== null) return;
    try {
      const savedStep = JSON.parse(localStorage.getItem(`kyc_step_${runnerId}`));
      const savedStatusRaw = localStorage.getItem(`kyc_status_${runnerId}`);
      const savedStatus = savedStatusRaw ? JSON.parse(savedStatusRaw) : null;

      if (savedStep !== null && savedStep !== undefined) {
        setKycStep(savedStep);
        // step 6 is ambiguous — it's used for both "pending review" and "verified"
        // UI. Only treat it as verified if kycStatus actually says so.
        if (savedStep === 6 && savedStatus?.overallVerified) {
          isAlreadyVerifiedRef.current = true;
          localStorage.setItem(`kyc_verified_shown_${runnerId}`, '1');
        }
      }

      if (savedStatus) {
        setKycStatus(savedStatus);
      }
    } catch { }
  }, [runnerId, kycStep]);

  useEffect(() => {
    if (!runnerId) return;
    // Only persist once we have meaningful state (avoid overwriting with the
    // default all-false object that fires on first mount before restore)
    if (kycStatus.overallVerified || kycStatus.selfieVerified || kycStatus.documentVerified) {
      try {
        localStorage.setItem(`kyc_status_${runnerId}`, JSON.stringify(kycStatus));
      } catch (_) { }
    }
  }, [kycStatus, runnerId]);

  useEffect(() => {
    if (!runnerId) return;
    const saved = localStorage.getItem(`kyc_doc_type_${runnerId}`);
    if (saved) {
      currentDocTypeRef.current = saved;
      setCurrentDocTypeState(saved);
    }
  }, [runnerId]);

  useEffect(() => {
    if (!runnerId || kycStep === null) return;
    localStorage.setItem(`kyc_step_${runnerId}`, JSON.stringify(kycStep));
  }, [kycStep, runnerId]);

  const fleetTypeRef = useRef(fleetType);
  useEffect(() => { fleetTypeRef.current = fleetType; }, [fleetType]);

  const setDocType = useCallback((type) => {
    currentDocTypeRef.current = type;
    setCurrentDocTypeState(type);
    if (runnerId) localStorage.setItem(`kyc_doc_type_${runnerId}`, type);
  }, [runnerId]);

  const startKycFlow = useCallback((setMessages) => {
    console.log('[KYC] startKycFlow called', { kycInitiated: kycInitiated.current, });
    if (kycInitiated.current) {
      console.log('[KYC] startKycFlow BLOCKED — already initiated');
      return
    };

    // block if already verified in local state
    if (kycStatus.overallVerified || kycStatus.selfieVerified) {
      console.log('[KYC] startKycFlow BLOCKED — already verified');
      kycInitiated.current = true;
      return;
    }

    kycInitiated.current = true;
    setKycStep(1);

    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: `kyc-${Date.now()}-1`,
        from: "them",
        text: "Before you can start rendering services, I need to verify you.",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "delivered",
        isKyc: true
      }]);

      setTimeout(() => {
        const idMessage = SINGLE_DOC_FLEETS.includes(fleetTypeRef.current)
          ? "To get you approved, I'll need a valid government ID, preferably NIN or a valid document."
          : fleetTypeRef.current === 'bike'
            ? "To get you approved, I'll need two valid documents — your NIN and a valid Biker's License."
            : "To get you approved, I'll need two valid government IDs. preferably NIN and a Driver's License.";

        setMessages(prev => [...prev, {
          id: `kyc-${Date.now()}-2`,
          from: "them",
          text: idMessage,
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered",
          isKyc: true,
        }]);

        setTimeout(() => {
          // Ask for NIN first for everyone
          setMessages(prev => [...prev, {
            id: `kyc-${Date.now()}-3`,
            from: "them",
            text: "Kindly provide your NIN.",
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "delivered",
            isKyc: true,
          }]);

          setDocType('nin');
          setTimeout(() => setKycStep(2), 700);
        }, 700);
      }, 700);
    }, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkVerificationStatus = useCallback(async (setMessages, onBanned, isReturning = false) => {
    console.log('[KYC] checkVerificationStatus called', { runnerId });
    if (!runnerId) {
      console.log('[KYC] checkVerificationStatus BLOCKED — no runnerId');
      return
    };
    try {
      const result = await dispatch(getVerificationStatus(runnerId));
      if (result.type.includes('rejected')) {
        // auth failure — stop polling silently
        if (result.payload?.status === 401) return;
      }
      if (!result.type.includes('fulfilled')) return;

      const { kycStatus, documents, biometrics } = result.payload;

      kycServerStatusRef.current = {
        nin: documents.nin?.status || 'not_submitted',
        driverLicense: documents.driverLicense?.status || 'not_submitted',
        bikerLicense: documents.bikerLicense?.status || 'not_submitted',
        selfie: biometrics.status || 'not_submitted',
      };

      if (kycStatus === 'banned') {
        onBanned?.();
        return;
      }

      const currentStatusKey = `${documents.nin?.status}-${documents.driverLicense?.status}-${documents.bikerLicense?.status}-${biometrics.status}-${kycStatus}`;

      if (lastCheckedStatusRef.current === currentStatusKey) return;
      lastCheckedStatusRef.current = currentStatusKey;

      if (isAlreadyVerifiedRef.current) {
        isAlreadyVerifiedRef.current = false;
        return;
      }

      // ── If everything is approved, show ONE combined message ─────────────
      const allApproved = biometrics.status === 'approved' && biometrics.selfieVerified;
      const effectivelyReturning = isReturning || isReturningUserRef.current;

      if (allApproved) {
        const shownKey = `kyc_verified_shown_${runnerId}`;
        const alreadyShown = localStorage.getItem(shownKey) === '1';

        if (!effectivelyReturning && !alreadyShown && !isAlreadyVerifiedRef.current) {
          localStorage.setItem(shownKey, '1'); // set immediately, before async setState
          setMessages(prev => {
            const hasIt = prev.some(m => m.text?.includes('Congratulations'));
            if (hasIt) return prev;
            return [...prev, {
              id: `kyc-verified-${Date.now()}`,
              from: "them",
              text: "Congratulations! Your documents have been verified.",
              time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              status: "delivered", isKyc: true
            }];
          });
        }

        isAlreadyVerifiedRef.current = true; // ← always set after allApproved

        setTimeout(() => {
          setKycStatus({ documentVerified: true, selfieVerified: true, overallVerified: true });
          dispatch(updateRunner({
            isVerifiedKyc: true,
            kycStatus: kycStatus
          }));
          setKycStep(6);
        }, 900);

        localStorage.removeItem(`kyc_step_${runnerId}`);
        localStorage.removeItem(`kyc_doc_type_${runnerId}`);
        return;
      }

      // ── Partial rejections/approvals
      const secondDocType = getSecondDocType(fleetTypeRef.current);
      const ninRejected = documents.nin?.status === 'rejected';
      const secondDocRejected = secondDocType && documents[secondDocType]?.status === 'rejected';
      const selfieRejected = biometrics.status === 'rejected';

      const allRejected = ninRejected && selfieRejected && (!secondDocType || secondDocRejected);

      let resumeStep = null;

      if (allRejected) {
        const reasons = [
          documents.nin.rejectionReason ? `NIN: ${documents.nin.rejectionReason}` : null,
          secondDocType && documents[secondDocType]?.rejectionReason ? `${secondDocLabel(secondDocType)}: ${documents[secondDocType].rejectionReason}` : null,
          biometrics.rejectionReason ? `Selfie: ${biometrics.rejectionReason}` : null,
        ].filter(Boolean).join(' | ');

        setMessages(prev => [...prev, {
          id: `kyc-all-rejected-${Date.now()}`,
          from: "them",
          text: `❌ Your documents and selfie were all rejected${reasons ? ` ${reasons}.` : ''}. Let's resubmit everything — starting with your NIN.`,
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered", isKyc: true
        }]);
        setDocType('nin');
        capturedIdPhotoRef.current = null;
        capturedSelfiePhotoRef.current = null;
        resumeStep = { step: 2, docType: 'nin' };
      } else {
        if (ninRejected) {
          const reason = documents.nin.rejectionReason
            ? `❌ Your NIN verification was unsuccessful: ${documents.nin.rejectionReason}. Please resubmit.`
            : "❌ Your NIN verification was unsuccessful. Please resubmit.";
          setMessages(prev => [...prev, {
            id: `kyc-nin-rejected-${Date.now()}`,
            from: "them", text: reason,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "delivered", isKyc: true
          }]);
          if (!resumeStep) resumeStep = { step: 2, docType: 'nin' };
        }

        if (secondDocRejected) {
          const doc = documents[secondDocType];
          const reason = doc.rejectionReason
            ? `❌ Your ${secondDocLabel(secondDocType)} verification was unsuccessful: ${doc.rejectionReason}. Please resubmit.`
            : `❌ Your ${secondDocLabel(secondDocType)} verification was unsuccessful. Please resubmit.`;

          setMessages(prev => [...prev, {
            id: `kyc-second-doc-rejected-${Date.now()}`,
            from: "them", text: reason,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "delivered", isKyc: true
          }]);
          if (!resumeStep) resumeStep = { step: 2, docType: secondDocType };
        }

        if (selfieRejected) {
          const reason = biometrics.rejectionReason
            ? `❌ Your selfie verification was unsuccessful: ${biometrics.rejectionReason}. Please resubmit.`
            : "❌ Your selfie verification was unsuccessful. Please resubmit.";
          setMessages(prev => [...prev, {
            id: `kyc-selfie-rejected-${Date.now()}`,
            from: "them", text: reason,
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "delivered", isKyc: true
          }]);
          if (!resumeStep) resumeStep = { step: 5, docType: null };
        }
      }

      // Apply only the highest-priority rejection
      if (resumeStep) {
        if (resumeStep.docType) {
          setDocType(resumeStep.docType);
          capturedIdPhotoRef.current = null;
        } else {
          capturedSelfiePhotoRef.current = null;
        }
        setKycStep(resumeStep.step);
      }

      if (kycStatus === 'banned') {
        setMessages(prev => [...prev, {
          id: `kyc-banned-${Date.now()}`,
          from: "them",
          text: "🚫 Your account has been suspended. Please contact support at support@sendrey.com.",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered", isKyc: true
        }]);
      }

    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  }, [dispatch, runnerId, setDocType]);

  const resumeKycFlow = useCallback((serverKycStatus, setMessages) => {
    isReturningUserRef.current = true;
    console.log('[KYC] resumeKycFlow called', { serverKycStatus, kycInitiated: kycInitiated.current, fleetType: fleetTypeRef.current });
    if (kycInitiated.current) {
      console.log('[KYC] resumeKycFlow BLOCKED — already initiated');
      return
    };

    const isFullyVerified = serverKycStatus?.selfieVerified ||
      serverKycStatus?.selfieStatus === 'pending_review' ||
      serverKycStatus?.overallVerified;

    if (isFullyVerified) {
      kycInitiated.current = true;
      setKycStatus({ documentVerified: true, selfieVerified: true, overallVerified: true });
      setKycStep(6);
      return;
    }

    kycInitiated.current = true;

    if (!isFullyVerified) {
      // only set the ref for partial states where we want to suppress duplicate status messages
      isAlreadyVerifiedRef.current = false;
    }

    const hasRejection =
      serverKycStatus?.ninStatus === 'rejected' ||
      serverKycStatus?.driverLicenseStatus === 'rejected' ||
      serverKycStatus?.bikerLicenseStatus === 'rejected' ||
      serverKycStatus?.selfieStatus === 'rejected';

    if (hasRejection) {
      checkVerificationStatus(setMessages, () => { }, true);
      return;
    }

    const resume = resolveResumeStep(serverKycStatus, fleetTypeRef.current);

    if (resume === null) {
      kycInitiated.current = false;
      startKycFlow(setMessages);
      return;
    }

    const step = typeof resume === 'object' ? resume.step : resume;
    const docType = typeof resume === 'object' ? resume.nextDoc : null;

    if (docType) {
      setDocType(docType);
      capturedIdPhotoRef.current = null; // clear any stale capture from a prior session
    }

    // ── Fully verified: set status + step silently, one clean message ────────
    if (step === 6) {
      isAlreadyVerifiedRef.current = true;
      setKycStatus({ documentVerified: true, selfieVerified: true, overallVerified: true });
      setKycStep(6);

      setMessages(prev => [...prev, {
        id: `kyc-resume-${Date.now()}`,
        from: "them",
        text: "Welcome back! Click the button below to connect to a user.",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "delivered",
        isKyc: true,
      }]);

      return;
    }

    if (step === 3) {
      capturedSelfiePhotoRef.current = null; // clear any stale capture from a prior session
    }

    const promptText = step === 3
      ? "Welcome back! You just need to take your selfie to complete verification."
      : docType
        ? `Welcome back! You still need to provide your ${secondDocLabel(docType)}.`
        : "Welcome back! Let's continue your verification.";

    setMessages(prev => [...prev, {
      id: `kyc-resume-${Date.now()}`,
      from: "them",
      text: promptText,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      status: "delivered",
      isKyc: true,
    }]);

    setKycStep(step);
  }, [startKycFlow, setDocType, checkVerificationStatus]);

  const onIdVerified = useCallback((photo, setMessages) => {

    if (!photo) {
      console.error('[KYC] onIdVerified called with no photo');
      const docLabel = currentDocTypeRef.current === 'driverLicense'
        ? "Driver's License"
        : currentDocTypeRef.current === 'bikerLicense'
          ? "Biker's License"
          : 'NIN';
      setMessages(prev => [...prev, {
        id: `kyc-err-${Date.now()}`,
        from: "them",
        text: `That didn't come through. Please try again with your ${docLabel}.`,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "delivered",
        isKyc: true
      }]);
      setKycStep(2);
      return;
    }

    capturedIdPhotoRef.current = photo;
    setKycStep(1);

    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: `kyc-img-${Date.now()}`,
        from: "me",
        type: "image",
        fileUrl: photo,
        text: "",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "sent",
        isKyc: true
      }]);

      // Auto-submit using currentDocTypeRef — no need to ask ID type
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: `kyc-submitting-${Date.now()}`,
          from: "them",
          text: "Submitting your document for verification...",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered",
          isKyc: true
        }]);

        handleIDTypeSelection(currentDocTypeRef.current, setMessages);
      }, 500);
    }, 500);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleIDTypeSelection = useCallback(async (idType, setMessages) => {
    if (verifyInProgress.current) return;

    const photo = capturedIdPhotoRef.current;

    if (!photo) {
      console.error('No photo captured!');
      const docLabel = currentDocTypeRef.current === 'driverLicense'
        ? "Driver's License"
        : currentDocTypeRef.current === 'bikerLicense'
          ? "Biker's License"
          : 'NIN';

      setMessages(prev => [...prev, {
        id: `kyc-err-${Date.now()}`,
        from: "them",
        text: `No photo found. Please try capturing or uploading your ${docLabel} again.`,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "delivered",
        isKyc: true
      }]);
      setKycStep(2);
      return;
    }

    verifyInProgress.current = true;
    setKycStep(1);

    setTimeout(async () => {
      const base64ToFile = (base64, filename) => {
        try {
          const base64Data = base64.replace(/^data:image\/(jpeg|jpg|png|webp);base64,/, '');
          const byteCharacters = atob(base64Data);
          const byteArray = new Uint8Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteArray[i] = byteCharacters.charCodeAt(i);
          }
          const blob = new Blob([byteArray], { type: 'image/jpeg' });
          return new File([blob], filename, { type: 'image/jpeg' });
        } catch (error) {
          console.error('Error converting base64 to file:', error);
          return null;
        }
      };

      const file = base64ToFile(photo, 'id_document.jpg');

      if (!file) {
        setMessages(prev => [...prev, {
          id: `kyc-err-${Date.now()}`,
          from: "them",
          text: "I couldn't process the image. Please try again.",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered",
          isKyc: true
        }]);
        setKycStep(2);
        verifyInProgress.current = false;
        return;
      }

      try {

        const result = currentDocTypeRef.current === 'driverLicense'
          ? await dispatch(verifyDriverLicense(file))
          : currentDocTypeRef.current === 'bikerLicense'
            ? await dispatch(verifyBikerLicense(file))
            : await dispatch(verifyNIN(file));
        verifyInProgress.current = false;

        if (result.type.includes('fulfilled')) {
          setMessages(prev => [...prev, {
            id: `kyc-success-${Date.now()}`,
            from: "them",
            text: "Your document has been submitted successfully and is under review. Kindly exercise patience, we'll get back to you soon.",
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "delivered",
            isKyc: true
          }]);

          // Optimistically mark what we just submitted as pending, so the next check reflects reality
          kycServerStatusRef.current[currentDocTypeRef.current] = 'pending_review';

          const secondDocType = getSecondDocType(fleetTypeRef.current);
          const needsSecondDoc = secondDocType &&
            currentDocTypeRef.current !== secondDocType &&
            ['not_submitted', 'rejected'].includes(kycServerStatusRef.current[secondDocType]);

          const selfieStatus = kycServerStatusRef.current.selfie;
          const needsSelfie = ['not_submitted', 'rejected'].includes(selfieStatus);

          if (needsSecondDoc) {
            const isResubmit = kycServerStatusRef.current[secondDocType] === 'rejected';
            setDocType(secondDocType);
            capturedIdPhotoRef.current = null;

            setTimeout(() => {
              setMessages(prev => [...prev, {
                id: `kyc-dl-prompt-${Date.now()}`,
                from: "them",
                text: isResubmit ? `Kindly resubmit your ${secondDocLabel(secondDocType)}.` : `Kindly provide your ${secondDocLabel(secondDocType)}.`,
                time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                status: "delivered",
                isKyc: true
              }]);
              setTimeout(() => setKycStep(2), 800);
            }, 1000);

          } else if (needsSelfie) {
            const isResubmit = selfieStatus === 'rejected';

            if (isResubmit) {
              capturedSelfiePhotoRef.current = null;
              setTimeout(() => {
                setMessages(prev => [...prev, {
                  id: `kyc-selfie-resubmit-prompt-${Date.now()}`,
                  from: "them",
                  text: "Kindly resubmit your selfie as well.",
                  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                  status: "delivered",
                  isKyc: true
                }]);
                setTimeout(() => setKycStep(5), 800);
              }, 1000);
            } else {
              setTimeout(() => {
                setMessages(prev => [...prev, {
                  id: `kyc-selfie-prompt-${Date.now()}`,
                  from: "them",
                  text: "You need to take a quick selfie so I can confirm it's really you.",
                  time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                  status: "delivered",
                  isKyc: true
                }]);
                setTimeout(() => setKycStep(3), 800);
              }, 1000);
            }

          } else {
            // Nothing else is missing or rejected — back to "under review", nothing more to collect
            setTimeout(() => setKycStep(6), 1000);
          }

        } else {
          setMessages(prev => [...prev, {
            id: `kyc-err-${Date.now()}`,
            from: "them",
            text: "Document submission failed. Please try again.",
            time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            status: "delivered",
            isKyc: true
          }]);
          setTimeout(() => setKycStep(2), 700);
        }
      } catch (error) {
        console.error('Dispatch error:', error);
        verifyInProgress.current = false;
        setMessages(prev => [...prev, {
          id: `kyc-err-${Date.now()}`,
          from: "them",
          text: "Document submission failed. Please try again.",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered",
          isKyc: true
        }]);
        setTimeout(() => setKycStep(2), 700);
      }
    }, 1500);
  }, [dispatch, setDocType]);

  const handleSelfieResponse = useCallback((response, setMessages) => {
    if (response === 'okay') {
      setTimeout(() => {
        setMessages(prev => [...prev, {
          id: `kyc-selfie-ok-${Date.now()}`,
          from: "them",
          text: "Please ensure your face is visible and there is enough light where you are, thanks!",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered",
          isKyc: true
        }]);
      }, 500);
      setTimeout(() => setKycStep(5), 600);
    }
  }, []);

  const onSelfieVerified = useCallback(async (photo, setMessages) => {
    capturedSelfiePhotoRef.current = photo;
    setKycStep(1);

    setTimeout(async () => {
      setMessages(prev => [...prev, {
        id: `kyc-selfie-img-${Date.now()}`,
        from: "me",
        type: "image",
        fileUrl: photo,
        text: "",
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        status: "sent",
        isKyc: true
      }]);

      setTimeout(async () => {
        setMessages(prev => [...prev, {
          id: `kyc-selfie-submitting-${Date.now()}`,
          from: "them",
          text: "Submitting your selfie for verification...",
          time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
          status: "delivered",
          isKyc: true
        }]);

        try {
          const res = await fetch(photo);
          const blob = await res.blob();
          const file = new File([blob], "selfie.jpg", { type: "image/jpeg" });
          const result = await dispatch(verifySelfie(file));
          kycServerStatusRef.current.selfie = 'pending_review';

          if (result.type.includes('fulfilled')) {
            setMessages(prev => [...prev, {
              id: `kyc-selfie-done-${Date.now()}`,
              from: "them",
              text: "Great! Your selfie has been submitted for review. You'll be notified once verification is complete.",
              time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              status: "delivered",
              isKyc: true
            }]);

            setKycStatus({ documentVerified: true, selfieVerified: true, overallVerified: false });
            setKycStep(6);

            localStorage.removeItem(`kyc_step_${runnerId}`);
            localStorage.removeItem(`kyc_doc_type_${runnerId}`);
          } else {
            const errorMsg = result.payload?.message || "Selfie submission failed. Please try again.";
            setMessages(prev => [...prev, {
              id: `kyc-selfie-err-${Date.now()}`,
              from: "them",
              text: errorMsg,
              time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
              status: "delivered",
              isKyc: true
            }]);
            setTimeout(() => setKycStep(5), 800);
          }
        } catch (err) {
          console.error('Selfie upload error:', err);
          setTimeout(() => setKycStep(5), 800);
        }
      }, 700);
    }, 500);
  }, [dispatch, runnerId]);

  const SELFIE_TRIGGERS = ['okay', 'alright', 'sure', 'yes', 'ok'];

  const handleRunnerMessage = useCallback((text, setMessages) => {
    const normalized = text.trim().toLowerCase();
    const isTrigger = SELFIE_TRIGGERS.includes(normalized);

    if (isTrigger && kycStep === 3) {
      setMessages(prev => [...prev, {
        id: `kyc-runner-${Date.now()}`,
        from: 'me',
        text,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        status: 'sent',
        isKyc: true
      }]);
      handleSelfieResponse('okay', setMessages);
      return true;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kycStep, handleSelfieResponse]);

  return {
    kycStep,
    kycStatus,
    setKycStep,
    capturedIdPhoto: capturedIdPhotoRef.current,
    currentDocType,
    startKycFlow,
    resumeKycFlow,
    onIdVerified,
    handleIDTypeSelection,
    handleSelfieResponse,
    onSelfieVerified,
    checkVerificationStatus,
    showConnectButton,
    setShowConnectButton,
    handleRunnerMessage
  };
};